import { NgZone } from '@angular/core';
import { SelectItem, SelectOption, SelectOptionSource } from '@siemens/element-ng/select';
import * as L from 'leaflet';
import { geocoders } from 'leaflet-control-geocoder';
import { debounceTime, distinctUntilChanged, Observable, of, Subject, switchMap } from 'rxjs';

export interface GeocodedPlace {
  id: string;
  name: string;
  center: L.LatLng;
  bbox: L.LatLngBounds;
}

/**
 * Geocoded places for `si-select`, mirroring how station search feeds the route planner.
 *
 * The data is unrelated to stations: this queries Nominatim for any place name. Only the
 * input behaviour is shared.
 */
export class PlaceSelectSource implements SelectOptionSource<string> {
  private readonly geocoder = geocoders.nominatim();
  private readonly searchTermSubject = new Subject<string>();
  private readonly cache = new Map<string, GeocodedPlace>();
  private readonly debouncedSearch$: Observable<SelectOption<string>[]>;

  constructor(private readonly zone: NgZone) {
    this.debouncedSearch$ = this.searchTermSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap(searchTerm => this.fetchOptions(searchTerm))
    );
  }

  getPlaceById(id: string): GeocodedPlace | undefined {
    return this.cache.get(id);
  }

  cleanup(): void {
    this.searchTermSubject.complete();
    this.cache.clear();
  }

  getOptionsForSearch(search: string): Observable<SelectItem<string>[]> {
    return new Observable<SelectItem<string>[]>(observer => {
      const subscription = this.debouncedSearch$.subscribe({
        next: options => {
          observer.next(options);
          observer.complete();
        },
        error: err => observer.error(err)
      });

      this.searchTermSubject.next(search);

      return () => subscription.unsubscribe();
    });
  }

  /** Results only ever come from a search, so a value is always already cached. */
  getOptionsForValues(values: string[]): Observable<SelectOption<string>[]> {
    if (!values?.length) {
      return of([]);
    }

    const cached = values
      .map(value => this.cache.get(value))
      .filter((place): place is GeocodedPlace => !!place)
      .map(place => ({ type: 'option' as const, value: place.id, label: place.name }));

    return of(cached);
  }

  private fetchOptions(searchTerm: string): Observable<SelectOption<string>[]> {
    const query = searchTerm?.trim();
    if (!query) {
      return of([]);
    }

    return new Observable<SelectOption<string>[]>(subscriber => {
      this.geocoder.geocode(query, results => {
        const options = results.map((result, index) => {
          // Nominatim returns no stable id here, so the position is used instead.
          const id = `${index}-${result.center.lat},${result.center.lng}`;
          this.cache.set(id, {
            id,
            name: result.name,
            center: result.center,
            bbox: result.bbox
          });
          return { type: 'option' as const, value: id, label: result.name };
        });

        // The geocoder uses a bare XHR, so its callback lands outside Angular's zone and
        // the select would never render the emitted options.
        this.zone.run(() => {
          subscriber.next(options);
          subscriber.complete();
        });
      });
    });
  }
}
